import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import { Col, Button } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import history from '../history';
import PageContainer from './PageContainer';
import Footer from './Footer';
import { modal } from './modal/ModalEmitter';
import Widgets from './widget';
import WidgetManager from './widget/WidgetManager';
import PanelWidget from './widget/PanelWidget';
import Auth from '../services/AuthService';
import { updateUserData } from '../actions/AppActions';

import '../styles/dashboard.scss';

const ResponsiveReactGridLayout = WidthProvider(Responsive);

class Dashboard extends Component {
  constructor(props) {
    super(props);

    this.layoutProps = {
      className: 'layout',
      rowHeight: 357,
      cols: { lg: 12, md: 12, sm: 12, xs: 1, xxs: 1 },
    };

    this.state = {
      editMode: false,
    };

    this.gridLayout = null;
  }

  getBreakPoint() {
    return Responsive.utils.getBreakpointFromWidth(Responsive.defaultProps.breakpoints, this.gridLayout.state.width);
  }

  restoreDefault = () => {
    modal.confirm({
      message: 'This action will revert any changes made to the dashboard.',
      callback: () => {
        this.props.updateUserData('widgets', {});
      },
    });
  };

  handleEditLayout = () => {
    this.setState({ editMode: !this.state.editMode });
  };

  handleAddWidget = () => {
    modal.info({
      title: 'Widget Preferences',
      message: <WidgetManager />,
      hideFooter: true,
    });
  };

  handleLayoutChange = layout => {
    if (!this.state.editMode) {
      return;
    }

    const widgets = { ...this.props.widgets };

    // check breakpoint so we know it's safe to persist layout
    const breakpoint = this.getBreakPoint();

    // atm, we can't persist xs, xxs layout changes safely
    if (['lg', 'md', 'sm'].includes(breakpoint)) {
      for (const row of layout) {
        if (widgets[row.i]) {
          widgets[row.i].layout = row;
        }
      }

      this.props.updateUserData('widgets', widgets);
    }
  };

  renderGridLayout() {
    const { editMode } = this.state;
    const widgets = _.defaultsDeep(this.props.widgets, Widgets);
    const widgetComponents = [];
    const widgetLayout = [];

    _.each(widgets, (widget, key) => {
      const { show, permissions, menu = [], layout, page } = widget;
      let title = widget.title;

      const useLayout = layout || { x: 0, y: 999, w: 6, h: 1 };

      if (!show || !Widgets[key]) {
        return;
      }

      if (!Auth.hasPermissionToMany(permissions)) {
        return;
      }

      widgetLayout.push({
        i: key,
        ...useLayout,
      });

      if (key === 'agenda') {
        title = <Link to="/SDC0">{`${moment().format('MMM Do')} Appointments`}</Link>;
      }

      if (page) {
        menu.push(<div onClick={() => history.push(`/${page.toUpperCase()}`)}>Page Source ({page})</div>);
      }

      widgetComponents.push({ Component: Widgets[key].widget, key, title, menu });
    });

    return (
      <ResponsiveReactGridLayout
        ref={ref => {
          this.gridLayout = ref;
        }}
        layouts={{ lg: widgetLayout, md: widgetLayout, sm: widgetLayout, xs: widgetLayout, xxs: widgetLayout }}
        {...this.layoutProps}
        onResizeStop={this.handleLayoutChange}
        onDragStop={this.handleLayoutChange}
        compactType="vertical"
        useCSSTransforms
        isDraggable={editMode}
        isResizable={editMode}
        margin={[15, 15]}
        containerPadding={[15, 15]}
      >
        {widgetComponents.map(({ Component, key, title, menu }) => {
          return (
            <div key={key} className="grid-item">
              <PanelWidget name={key} header={title} menu={menu}>
                <Component />
              </PanelWidget>
            </div>
          );
        })}
      </ResponsiveReactGridLayout>
    );
  }

  renderToolbar() {
    const { editMode } = this.state;

    if (editMode) {
      return (
        <Col className="widgets-toolbar">
          <Button variant="secondary" onClick={this.restoreDefault} className="float-right">
            Restore Defaults
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              this.setState({ editMode: !this.state.editMode });
            }}
          >
            Done
          </Button>
          &nbsp;
          <Button variant="success" onClick={this.handleAddWidget}>
            Add Widget
          </Button>
        </Col>
      );
    }

    return (
      <Col className="widgets-toolbar d-none d-xl-block" sm={12} md={12} lg={{ span: 2, offset: 5 }}>
        <Button variant="secondary" block onClick={this.handleEditLayout}>
          <i className="zmdi zmdi-view-dashboard" />
          &nbsp; Manage Widgets
        </Button>
      </Col>
    );
  }

  render() {
    return (
      <PageContainer disableSidebar>
        <Helmet title="Blaze - Dashboard" />
        <div id="dashboard">
          <Widgets.menu.widget />
          <div className="widgets">{this.renderGridLayout()}</div>
          {this.renderToolbar()}
          <div className="clearfix" />
        </div>
        <Footer />
      </PageContainer>
    );
  }
}

Dashboard.propTypes = {
  updateUserData: PropTypes.func,
  widgets: PropTypes.object,
};

const mapStateToProps = state => {
  return {
    widgets: state.app.userData.widgets,
    dashboard: state.app.userData.dashboard,
  };
};

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ updateUserData }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
